import { useEffect, useState } from "react";
import { toast } from "sonner";
import { Lock, Unlock, Pencil, Trash2, Plus, FileDown, KeyRound, Copy } from "lucide-react";
import { api, fmtUSD, fmtDate, API } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "@/components/ui/dialog";
import { Textarea } from "@/components/ui/textarea";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";

const empty = {
  email: "",
  full_name: "",
  phone: "",
  country: "",
  bank_details: "",
  btc_address: "",
  usdt_address: "",
  usdt_network: "TRC20",
  bank_name: "",
  bank_ifsc: "",
  bank_account_name: "",
  bank_account_number: "",
  bank_swift: "",
  password: "",
  initial_balance: 0,
  leverage: 100,
};

export default function Users() {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(empty);
  const [resetInfo, setResetInfo] = useState(null); // {email, new_password}

  const load = async () => {
    setLoading(true);
    const { data } = await api.get("/admin/users");
    setUsers(data);
    setLoading(false);
  };
  useEffect(() => {
    load();
  }, []);

  const openCreate = () => {
    setEditing(null);
    setForm(empty);
    setOpen(true);
  };
  const openEdit = (u) => {
    setEditing(u);
    setForm({
      email: u.email,
      full_name: u.full_name,
      phone: u.phone || "",
      country: u.country || "",
      bank_details: u.bank_details || "",
      btc_address: u.btc_address || "",
      usdt_address: u.usdt_address || "",
      usdt_network: u.usdt_network || "TRC20",
      bank_name: u.bank_name || "",
      bank_ifsc: u.bank_ifsc || "",
      bank_account_name: u.bank_account_name || "",
      bank_account_number: u.bank_account_number || "",
      bank_swift: u.bank_swift || "",
      password: "",
      initial_balance: u.balance,
      leverage: u.leverage,
    });
    setOpen(true);
  };

  const submit = async () => {
    try {
      if (editing) {
        const body = { ...form };
        if (!body.password) delete body.password;
        delete body.initial_balance;
        body.balance = form.initial_balance;
        await api.patch(`/admin/users/${editing.id}`, body);
        toast.success("Client updated");
      } else {
        await api.post("/admin/users", form);
        toast.success("Client created");
      }
      setOpen(false);
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const toggleBlock = async (u) => {
    try {
      const path = u.blocked ? "unblock" : "block";
      await api.post(`/admin/users/${u.id}/${path}`);
      toast.success(u.blocked ? "Client unblocked" : "Client blocked");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const remove = async (u) => {
    if (!window.confirm(`Delete ${u.full_name}? This removes all trades/transactions.`)) return;
    try {
      await api.delete(`/admin/users/${u.id}`);
      toast.success("Client deleted");
      load();
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed");
    }
  };

  const downloadStatement = async (u) => {
    try {
      const res = await api.get(`/admin/users/${u.id}/statement`, { responseType: "blob" });
      const url = URL.createObjectURL(new Blob([res.data], { type: "application/pdf" }));
      const a = document.createElement("a");
      a.href = url;
      a.download = `xm-forex-statement-${u.email}.pdf`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
      toast.success(`Statement for ${u.full_name} downloaded`);
    } catch (e) {
      toast.error("Failed to generate statement");
    }
  };

  const resetPassword = async (u) => {
    if (!window.confirm(`Reset password for ${u.full_name}? A new random password will be generated and shown ONCE.`)) return;
    try {
      const { data } = await api.post(`/admin/users/${u.id}/reset-password`);
      setResetInfo(data);
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Failed to reset password");
    }
  };

  const copyResetPw = async () => {
    if (!resetInfo?.new_password) return;
    try {
      await navigator.clipboard.writeText(resetInfo.new_password);
      toast.success("Password copied to clipboard");
    } catch {
      toast.error("Copy failed");
    }
  };

  return (
    <div className="space-y-6" data-testid="users-page">
      <div className="flex items-center justify-between">
        <div>
          <p className="label-eyebrow">{users.length} clients</p>
          <h3 className="text-lg font-semibold">Client Accounts</h3>
        </div>
        <Button onClick={openCreate} data-testid="add-user-btn" className="gap-2">
          <Plus className="w-4 h-4" /> Add Client
        </Button>
      </div>

      <div className="border border-subtle bg-card overflow-x-auto">
        <Table>
          <TableHeader>
            <TableRow className="bg-surface-hover border-subtle">
              <TableHead>Client</TableHead>
              <TableHead>Email</TableHead>
              <TableHead>Phone / Country</TableHead>
              <TableHead className="text-right">Balance</TableHead>
              <TableHead className="text-right">Leverage</TableHead>
              <TableHead>Status</TableHead>
              <TableHead>Joined</TableHead>
              <TableHead className="text-right">Actions</TableHead>
            </TableRow>
          </TableHeader>
          <TableBody>
            {loading && (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">Loading…</TableCell></TableRow>
            )}
            {!loading && users.length === 0 && (
              <TableRow><TableCell colSpan={8} className="text-center py-12 text-muted-foreground">No clients yet</TableCell></TableRow>
            )}
            {users.map((u) => (
              <TableRow key={u.id} data-testid={`user-row-${u.id}`} className="border-subtle hover:bg-surface-hover">
                <TableCell className="font-medium">{u.full_name}</TableCell>
                <TableCell className="font-mono-num text-sm">{u.email}</TableCell>
                <TableCell className="text-sm text-muted-foreground">
                  <div>{u.phone || "—"}</div>
                  <div className="text-xs">{u.country || "—"}</div>
                </TableCell>
                <TableCell className="text-right font-mono-num">{fmtUSD(u.balance)}</TableCell>
                <TableCell className="text-right font-mono-num">1:{u.leverage}</TableCell>
                <TableCell>
                  <span className={`inline-flex items-center gap-1.5 text-xs font-semibold px-2 py-1 rounded ${u.blocked ? "bg-loss/15 text-loss" : "bg-profit/15 text-profit"}`}>
                    <span className={`w-1.5 h-1.5 rounded-full ${u.blocked ? "bg-loss" : "bg-profit"}`} />
                    {u.blocked ? "BLOCKED" : "ACTIVE"}
                  </span>
                </TableCell>
                <TableCell className="text-xs text-muted-foreground">{fmtDate(u.created_at)}</TableCell>
                <TableCell className="text-right">
                  <div className="flex items-center gap-1 justify-end">
                    <button onClick={() => openEdit(u)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`edit-user-${u.id}`} title="Edit">
                      <Pencil className="w-3.5 h-3.5" />
                    </button>
                    <button onClick={() => resetPassword(u)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`reset-pw-${u.id}`} title="Reset password">
                      <KeyRound className="w-3.5 h-3.5 text-warning" />
                    </button>
                    <button onClick={() => downloadStatement(u)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`statement-user-${u.id}`} title="Download PDF statement">
                      <FileDown className="w-3.5 h-3.5 text-primary" />
                    </button>
                    <button onClick={() => toggleBlock(u)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`toggle-block-${u.id}`} title={u.blocked ? "Unblock" : "Block"}>
                      {u.blocked ? <Unlock className="w-3.5 h-3.5 text-profit" /> : <Lock className="w-3.5 h-3.5 text-warning" />}
                    </button>
                    <button onClick={() => remove(u)} className="p-1.5 hover:bg-surface-hover rounded" data-testid={`delete-user-${u.id}`} title="Delete">
                      <Trash2 className="w-3.5 h-3.5 text-loss" />
                    </button>
                  </div>
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="bg-card border-subtle max-w-lg max-h-[90vh] p-0 flex flex-col" data-testid="user-dialog">
          <DialogHeader className="px-6 pt-6 pb-2 shrink-0">
            <DialogTitle>{editing ? "Edit Client" : "Add Client"}</DialogTitle>
            <DialogDescription className="text-muted-foreground">
              {editing ? "Update client details, credentials and bank information." : "Create a new client account with contact and bank details."}
            </DialogDescription>
          </DialogHeader>
          <div className="grid grid-cols-2 gap-4 px-6 py-2 overflow-y-auto flex-1">
            <div className="col-span-2">
              <Label className="label-eyebrow">Full Name</Label>
              <Input value={form.full_name} onChange={(e) => setForm({ ...form, full_name: e.target.value })} data-testid="form-full-name" className="mt-2 bg-background border-subtle" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Email</Label>
              <Input type="email" value={form.email} onChange={(e) => setForm({ ...form, email: e.target.value })} data-testid="form-email" className="mt-2 bg-background border-subtle" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Password {editing && "(leave blank to keep)"}</Label>
              <Input value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} data-testid="form-password" className="mt-2 bg-background border-subtle" />
            </div>
            <div>
              <Label className="label-eyebrow">Mobile Number</Label>
              <Input value={form.phone} onChange={(e) => setForm({ ...form, phone: e.target.value })} data-testid="form-phone" className="mt-2 bg-background border-subtle font-mono-num" placeholder="+1-555-0100" />
            </div>
            <div>
              <Label className="label-eyebrow">Country</Label>
              <Input value={form.country} onChange={(e) => setForm({ ...form, country: e.target.value })} data-testid="form-country" className="mt-2 bg-background border-subtle" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">Bank Details (free-form notes)</Label>
              <Textarea
                value={form.bank_details}
                onChange={(e) => setForm({ ...form, bank_details: e.target.value })}
                data-testid="form-bank-details"
                className="mt-2 bg-background border-subtle font-mono-num text-sm"
                rows={2}
                placeholder="Optional additional notes about payout"
              />
            </div>

            <div className="col-span-2 pt-2">
              <p className="label-eyebrow mb-1">Deposit Wallets (visible to client)</p>
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">BTC Wallet Address</Label>
              <Input value={form.btc_address} onChange={(e) => setForm({ ...form, btc_address: e.target.value })} data-testid="form-btc" className="mt-2 bg-background border-subtle font-mono-num text-xs" placeholder="bc1q..." />
            </div>
            <div>
              <Label className="label-eyebrow">USDT Wallet Address</Label>
              <Input value={form.usdt_address} onChange={(e) => setForm({ ...form, usdt_address: e.target.value })} data-testid="form-usdt" className="mt-2 bg-background border-subtle font-mono-num text-xs" placeholder="Txxxx / 0xxxxx" />
            </div>
            <div>
              <Label className="label-eyebrow">USDT Network</Label>
              <Input value={form.usdt_network} onChange={(e) => setForm({ ...form, usdt_network: e.target.value })} data-testid="form-usdt-network" className="mt-2 bg-background border-subtle font-mono-num" placeholder="TRC20 / ERC20 / BEP20" />
            </div>

            <div className="col-span-2 pt-2">
              <p className="label-eyebrow mb-1">Bank Payout Details (used for withdrawals)</p>
            </div>
            <div>
              <Label className="label-eyebrow">Bank Name</Label>
              <Input value={form.bank_name} onChange={(e) => setForm({ ...form, bank_name: e.target.value })} data-testid="form-bank-name" className="mt-2 bg-background border-subtle" />
            </div>
            <div>
              <Label className="label-eyebrow">Account Holder</Label>
              <Input value={form.bank_account_name} onChange={(e) => setForm({ ...form, bank_account_name: e.target.value })} data-testid="form-bank-holder" className="mt-2 bg-background border-subtle" />
            </div>
            <div>
              <Label className="label-eyebrow">Account Number</Label>
              <Input value={form.bank_account_number} onChange={(e) => setForm({ ...form, bank_account_number: e.target.value })} data-testid="form-bank-acc" className="mt-2 bg-background border-subtle font-mono-num" />
            </div>
            <div>
              <Label className="label-eyebrow">IFSC / Routing</Label>
              <Input value={form.bank_ifsc} onChange={(e) => setForm({ ...form, bank_ifsc: e.target.value })} data-testid="form-bank-ifsc" className="mt-2 bg-background border-subtle font-mono-num" />
            </div>
            <div className="col-span-2">
              <Label className="label-eyebrow">SWIFT / BIC</Label>
              <Input value={form.bank_swift} onChange={(e) => setForm({ ...form, bank_swift: e.target.value })} data-testid="form-bank-swift" className="mt-2 bg-background border-subtle font-mono-num" placeholder="Optional (for international transfers)" />
            </div>
            <div>
              <Label className="label-eyebrow">{editing ? "Balance (USD)" : "Initial Balance (USD)"}</Label>
              <Input type="number" step="0.01" value={form.initial_balance} onChange={(e) => setForm({ ...form, initial_balance: parseFloat(e.target.value) || 0 })} data-testid="form-balance" className="mt-2 bg-background border-subtle font-mono-num" />
            </div>
            <div>
              <Label className="label-eyebrow">Leverage</Label>
              <Input type="number" value={form.leverage} onChange={(e) => setForm({ ...form, leverage: parseInt(e.target.value) || 100 })} data-testid="form-leverage" className="mt-2 bg-background border-subtle font-mono-num" />
            </div>
          </div>
          <DialogFooter className="px-6 pb-6 pt-3 border-t border-subtle shrink-0">
            <Button variant="outline" onClick={() => setOpen(false)} data-testid="user-cancel">Cancel</Button>
            <Button onClick={submit} data-testid="user-save">{editing ? "Save Changes" : "Create Client"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Reset-password result — one-time reveal */}
      <Dialog open={!!resetInfo} onOpenChange={(v) => !v && setResetInfo(null)}>
        <DialogContent className="bg-card border-subtle max-w-md" data-testid="reset-pw-dialog">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <KeyRound className="w-4 h-4 text-warning" /> Temporary Password
            </DialogTitle>
            <DialogDescription className="text-muted-foreground">
              Share this password with <span className="font-mono-num text-foreground">{resetInfo?.email}</span> over a secure channel. It will not be shown again.
            </DialogDescription>
          </DialogHeader>
          <div className="flex items-center gap-2 border border-subtle bg-background rounded p-3">
            <code
              className="font-mono-num text-lg tracking-wider flex-1 select-all break-all"
              data-testid="reset-pw-value"
            >
              {resetInfo?.new_password}
            </code>
            <button
              onClick={copyResetPw}
              className="p-2 hover:bg-surface-hover rounded"
              data-testid="reset-pw-copy"
              title="Copy to clipboard"
            >
              <Copy className="w-4 h-4" />
            </button>
          </div>
          <p className="text-xs text-warning">
            Advise the client to change this password on next login (Profile → Security).
          </p>
          <DialogFooter>
            <Button onClick={() => setResetInfo(null)} data-testid="reset-pw-close">Done</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}
